import { OwnerFieldFragment } from 'generated/resources/graphql'

import { Employee } from 'modules/core/hooks/useCurrentSession'

import { getAdditionalInformationAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/AdditionalInformation/form/form'
import { getOwnerFieldDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/form/OwnerField/form'
import { getBudgetDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Budget/form/form'
import { getCategoryDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Category/form/form'
import { getCostCenterDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/form/form'
import { getDateTimeRangeAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/DateTimeRange/form/form'
import { getEmployeeDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Employee/form'
import { getGroupDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Group/form/form'
import { getPerDiemRatesDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/PerDiemRates/form/form'
import { getProjectsDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Projects/form/form'
import { getTripInformationAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/form/form'
import { getFilesDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Files/form/form'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { Expensable } from 'modules/expenses/types/Expensable'

export type PerDiemFormValues = ReturnType<typeof getPerDiemFormDefaultValues>

export interface GetPerDiemFormDefaultValuesParams {
  employee: Employee
  customizationFields: CustomizationField[]
  expensable?: Expensable
  groupId?: number | null
  groupCurrency?: string | null
  isNewPerDiemWizardEnabled?: boolean
}

export const getPerDiemFormDefaultValues = ({
  employee,
  groupId,
  groupCurrency,
  expensable,
  isNewPerDiemWizardEnabled = false,
}: GetPerDiemFormDefaultValuesParams) => {
  const additionalInformation = {
    internalReference: expensable?.internalReference ?? null,
    description: expensable?.description ?? null,
  }

  const budgetInformation = {
    bugdetMovement: {
      budget: {
        id: expensable?.bugdetMovement?.budget?.id ?? 0,
      },
    },
  }

  const expensableData = expensable ?? {
    perDiem: null,
    mileage: null,
  }
  const ownerInformation: OwnerFieldFragment = {
    owner: {
      employeeName: expensable?.owner?.employeeName,
    },
  }

  const perDiemRates = {
    currency: expensable?.currency || groupCurrency || employee?.legalEntity?.currency || 'EUR',
    perDiem: {
      perDiemRates: expensable?.perDiem?.perDiemRates || [
        {
          id: null,
          rateConfiguration: {
            id: -1,
            amountCents: 0,
            currency:
              expensable?.currency || groupCurrency || employee?.legalEntity?.currency || 'EUR',
            name: '',
          },
          rateConfigurationId: -1,
          units: 1,
          totalAmount: 0,
          rateAmount: 0,
        },
      ],
    },
  }

  const projectsInformation = {
    projects: [...(expensable?.projects || [])],
  }

  const files = {
    perDiem: {
      files: expensable?.perDiem?.files || [],
    },
  }

  const categoryInformation = {
    perDiem: {
      category: { id: expensable?.perDiem?.category?.id ?? '' },
      subcategory: expensable?.perDiem?.subcategory ?? '',
    },
  }

  const defaultValues = {
    ...getEmployeeDefaultValues(employee, expensable),
    ...getAdditionalInformationAttributes(additionalInformation),
    ...getBudgetDefaultValues(budgetInformation),
    ...getTripInformationAttributes(expensableData),
    ...getDateTimeRangeAttributes(expensableData),
    ...getPerDiemRatesDefaultValues(perDiemRates),
    ...getProjectsDefaultValues(projectsInformation),
    ...getFilesDefaultValues(files),
    ...getCategoryDefaultValues(categoryInformation),
    ...getOwnerFieldDefaultValues(ownerInformation),
    ...getGroupDefaultValues(groupId),
    ...getCostCenterDefaultValues(expensable),
  }

  if (isNewPerDiemWizardEnabled) {
    return {
      ...defaultValues,
      amount: expensable?.amount ? parseInt(expensable.amount) : 0,
      perDiemRates: [],
      perDiem: {
        startDate: expensable?.perDiem?.startDate ?? null,
        endDate: expensable?.perDiem?.endDate ?? null,
        perDiemDestinations: expensable?.perDiem?.perDiemDestinations ?? [],
        tripName: expensable?.perDiem?.tripName ?? null,
      },
    }
  }

  return defaultValues
}
